import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import StageOne from './StageOne';
import StageTwo from './StageTwo';
import Success from './Success';
import '../scss/register.scss';
import logo from '../images/logo.svg';

function RegisterContainer() {
  // Helper function to validate phone number format
  const isValidPhoneFormat = (phone) => {
    if (!phone) return false;
    // Check if phone starts with + or 0
    if (!phone.startsWith('+') && !phone.startsWith('0')) return false;
    // Basic format validation
    const phoneRegex = /^(\+\d{1,3}|0)\d{10,15}$/;
    return phoneRegex.test(phone);
  };

  const [stage, setStage] = useState(1);
  const [inviteCode, setInviteCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [isValidCode, setIsValidCode] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const location = useLocation();
  
  // Basic information (Stage 1)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
  });
  
  // Store player ID for updates
  const [playerId, setPlayerId] = useState(null);

  // Player abilities (Stage 2)
  const [playerAbilities, setPlayerAbilities] = useState({
    preferredFoot: 'Right',
    position: 'Midfielder',
    pace: 50,
    shooting: 50,
    passing: 50,
    dribbling: 50,
    defending: 50,
    physical: 50
  });

  // For existing player login
  const [existingPhone, setExistingPhone] = useState('');
  const [phoneCheckError, setPhoneCheckError] = useState('');
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  
  // For pre-populating player data
  const [isLoadingPlayerData, setIsLoadingPlayerData] = useState(false);

  useEffect(() => {
    // Check for code in URL parameters
    const params = new URLSearchParams(location.search);
    const codeFromUrl = params.get('code');
    
    if (codeFromUrl) {
      // Set the full code and let validation handle the parsing
      setInviteCode(codeFromUrl);
      
      // Validate the code immediately
      if (validateAndSetCode(codeFromUrl)) {
        setIsValidCode(true);
      }
    }
  }, [location]);

  // Validate invite code
  const validateAndSetCode = (code) => {
    // Clear any previous errors
    setCodeError('');
    
    // Check if code matches format: number-digits or number-digits-playerId
    const codePattern = /^(\d+)-(\d{4})(?:-(\d+))?$/;
    const match = code.match(codePattern);
    
    if (!match) {
      setCodeError('Invalid code format. Expected: team-code (e.g., 3-1234) or team-code-playerId (e.g., 3-1234-796)');
      return false;
    }

    const [, teamId, digits, playerId] = match;
    
    // Calculate sum of digits
    const sum = digits.split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    
    if (sum === 10) {
      // If player ID is present, extract it and fetch player data
      if (playerId) {
        fetchPlayerData(playerId);
      }
      return true;
    } else {
      setCodeError('Invalid invite code');
      return false;
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (validateAndSetCode(inviteCode)) {
      setIsValidCode(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'inviteCode') {
      setInviteCode(value);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleStageOneSubmit = (updatedFormData) => {
    setFormData(updatedFormData);
    setStage(2);
    // Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStageTwoSubmit = async (updatedPlayerAbilities) => {
    setPlayerAbilities(updatedPlayerAbilities);
    
    try {
      // Extract team ID from invite code (handle both old and new formats)
      let teamId = 1; // Default value
      const match = inviteCode.match(/^(\d+)-(\d{4})(?:-(\d+))?$/);
      if (match) {
        teamId = parseInt(match[1]);
      }
      
      const submissionData = {
        ...formData,
        ...updatedPlayerAbilities,
        inviteCode,
        teamId // Include the extracted team ID
      };
      
      // Include player_id if this is an update
      if (playerId) {
        submissionData.player_id = playerId;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();
      if (result.success) {
        setIsRegistered(true);
      } else {
        alert('Registration failed: ' + result.message);
      }
    } catch (error) {
      console.error('Error during registration:', error);
      alert('An error occurred during registration');
    }
  };

  const fetchPlayerData = async (playerIdParam) => {
    // Prevent duplicate calls
    if (playerId === playerIdParam) {
      return;
    }
    
    setIsLoadingPlayerData(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register/get_player_by_id.php?player_id=${playerIdParam}`);
      const data = await response.json();
      
      if (data.success && data.player) {
        const player = data.player;
        
        // Store the player ID for updates
        setPlayerId(player.player_id);
        
        // Pre-populate form data with player details
        setFormData({
          firstName: player.first_name || '',
          lastName: player.last_name || '',
          phone: player.phone || '',
          email: player.email || '',
          password: '', // Don't pre-populate password for security
        });
        
        // Pre-populate player abilities
        setPlayerAbilities({
          preferredFoot: player.preferred_foot || 'Right',
          position: player.position || 'Midfielder',
          pace: player.pace || 50,
          shooting: player.shooting || 50,
          passing: player.passing || 50,
          dribbling: player.dribbling || 50,
          defending: player.defending || 50,
          physical: player.physical || 50
        });
      }
    } catch (error) {
      console.error('Error fetching player data:', error);
    } finally {
      setIsLoadingPlayerData(false);
    }
  };

  const checkExistingPhone = async (e) => {
    e.preventDefault();
    setIsCheckingPhone(true);
    setPhoneCheckError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register/check_player.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phone: existingPhone,
          invite_code: inviteCode 
        })
      });

      const data = await response.json();
      
      if (data.exists) {
        // Redirect to login with phone prefilled
        window.location.href = `/?phone=${encodeURIComponent(existingPhone)}`;
      } else {
        setPhoneCheckError('No account found with this phone number.');
      }
    } catch (error) {
      console.error('Error checking phone:', error);
      setPhoneCheckError('Error checking phone. Please try again.');
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const renderCodeVerification = () => (
    <div className="registration-options">

      {/* Invite Code Section - hide when code is valid */}
      {!isValidCode && (
        <div className="invite-code-section">
          <h3>Enter Invite Code</h3>
          <form onSubmit={handleCodeSubmit}>
            <div className="form-group">
              <input 
                type="text" 
                name="inviteCode" 
                value={inviteCode} 
                onChange={handleChange}
                maxLength={15}
                placeholder="e.g., 3-1234 or 3-1234-796"
                required 
              />
              {codeError && <span className="error">{codeError}</span>}
            </div>
            <button type="submit" disabled={isValidCode}>Verify Code</button>
          </form>
        </div>
      )}

      {isValidCode && (
        <>
          {/* Existing Player Section */}
          <div className="existing-player-section">
            <h3>Already have an account?</h3>
            <form onSubmit={checkExistingPhone} className="phone-check-form">
              <div className="form-group">
                <input 
                  type="tel" 
                  value={existingPhone}
                  onChange={(e) => setExistingPhone(e.target.value)}
                  placeholder="Enter your phone number (e.g. +441234567890 or 07123456789)"
                  required
                />
                {phoneCheckError && <span className="error">{phoneCheckError}</span>}
              </div>
              <button type="submit" disabled={isCheckingPhone || !isValidPhoneFormat(existingPhone)}>
                {isCheckingPhone ? 'Checking...' : 'Login'}
              </button>
            </form>
          </div>

          {/* New Player Button */}
          <div className="new-player-section">
            <button onClick={() => setStage(1)} className="register-button">
              Register as New Player <span className="chevron-right">→</span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderContent = () => {
    if (isRegistered) {
      return <Success />;
    }

    if (isLoadingPlayerData) {
      return (
        <div className="loading-container">
          <div className="loading">Loading player details...</div>
        </div>
      );
    }

    if (!isValidCode) {
      return renderCodeVerification();
    }

    switch (stage) {
      case 1:
        return <StageOne 
          formData={formData} 
          onSubmit={handleStageOneSubmit} 
          inviteCode={inviteCode}
          isUpdate={!!playerId}
        />;
      case 2:
        return <StageTwo 
          playerAbilities={playerAbilities} 
          onSubmit={handleStageTwoSubmit} 
          onBack={() => setStage(1)}
          inviteCode={inviteCode}
          isUpdate={!!playerId}
        />;
      default:
        return renderCodeVerification();
    }
  };

  // Add useEffect to scroll to top when stage changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stage]);

  return (
    <div className="register-container container">
      <h2 className="audiowide">Register</h2>
      {renderContent()}
    </div>
  );
}

export default RegisterContainer; 